<template>
  <div class="dashboard-container">
    <!-- 顶部标题和全局控制 -->
    <div class="dashboard-header">
      <div class="header-main">
        <h1 class="dashboard-title">TX广告数据驾驶舱</h1>
        <p class="dashboard-subtitle">全方位数据洞察与智能分析</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" size="large" @click="refreshAllData" :loading="refreshing">
          <el-icon>
            <Refresh />
          </el-icon>
          刷新数据
        </el-button>
      </div>
    </div>

    <!-- 数据概览卡片 -->
    <div class="overview-cards">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="12" :md="6">
          <div class="stat-card primary" @click="activeTab = 'auth'">
            <div class="card-icon">
              <el-icon>
                <User />
              </el-icon>
            </div>
            <div class="card-content">
              <div class="card-value">{{ overviewData.totalAuthAccounts || 0 }}</div>
              <div class="card-label">授权账号</div>
              <div class="card-desc">活跃: {{ getActiveAuthCount() }}</div>
            </div>
            <div class="card-trend">
              <span class="trend-up" v-if="overviewData.todayAuthAccounts > 0">+{{ overviewData.todayAuthAccounts
                }}</span>
              <span class="trend-neutral" v-else>0</span>
            </div>
          </div>
        </el-col>

        <el-col :xs="24" :sm="12" :md="6">
          <div class="stat-card success" @click="activeTab = 'advertisers'">
            <div class="card-icon">
              <el-icon>
                <OfficeBuilding />
              </el-icon>
            </div>
            <div class="card-content">
              <div class="card-value">{{ overviewData.totalAdvertisers || 0 }}</div>
              <div class="card-label">广告商数量</div>
              <div class="card-desc">正常: {{ overviewData.normalAdvertisers || 0 }}</div>
            </div>
            <div class="card-trend">
              <span class="trend-up" v-if="overviewData.todayAdvertisers > 0">+{{ overviewData.todayAdvertisers
                }}</span>
              <span class="trend-neutral" v-else>0</span>
            </div>
          </div>
        </el-col>

        <el-col :xs="24" :sm="12" :md="6">
          <div class="stat-card warning" @click="activeTab = 'ads'">
            <div class="card-icon">
              <el-icon>
                <Promotion />
              </el-icon>
            </div>
            <div class="card-content">
              <div class="card-value">{{ overviewData.totalAds || 0 }}</div>
              <div class="card-label">广告总数</div>
              <div class="card-desc">活跃: {{ overviewData.activeAds || 0 }}</div>
            </div>
            <div class="card-trend">
              <span class="trend-up" v-if="overviewData.todayAds > 0">+{{ overviewData.todayAds }}</span>
              <span class="trend-neutral" v-else>0</span>
            </div>
          </div>
        </el-col>

        <el-col :xs="24" :sm="12" :md="6">
          <div class="stat-card danger" @click="activeTab = 'previews'">
            <div class="card-icon">
              <el-icon>
                <DataAnalysis />
              </el-icon>
            </div>
            <div class="card-content">
              <div class="card-value">{{ overviewData.totalPreviews || 0 }}</div>
              <div class="card-label">推广次数</div>
              <div class="card-desc">今日: {{ overviewData.todayPreviews || 0 }}</div>
            </div>
            <div class="card-trend">
              <span class="trend-up" v-if="overviewData.todayPreviews > 0">+{{ overviewData.todayPreviews }}</span>
              <span class="trend-neutral" v-else>0</span>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 图表区域 -->
    <div class="charts-section">
      <el-row :gutter="20">
        <!-- 广告商状态分布 -->
        <el-col :xs="24" :lg="8">
          <div class="chart-card">
            <div class="chart-header">
              <h3>广告商状态分布</h3>
              <el-tag type="primary">实时</el-tag>
            </div>
            <div class="chart-container">
              <div ref="advertiserStatusChart" style="height: 300px;"></div>
            </div>
          </div>
        </el-col>

        <!-- 广告状态分布 -->
        <el-col :xs="24" :lg="8">
          <div class="chart-card">
            <div class="chart-header">
              <h3>广告状态分布</h3>
              <el-tag type="success">活跃</el-tag>
            </div>
            <div class="chart-container">
              <div ref="adStatusChart" style="height: 300px;"></div>
            </div>
          </div>
        </el-col>

        <!-- 推广趋势 -->
        <el-col :xs="24" :lg="8">
          <div class="chart-card">
            <div class="chart-header">
              <h3>推广趋势</h3>
              <el-tag type="warning">7天</el-tag>
            </div>
            <div class="chart-container">
              <div ref="previewTrendChart" style="height: 300px;"></div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 详细数据表格 -->
    <div class="data-tables">
      <el-tabs v-model="activeTab" type="border-card" class="dashboard-tabs">
        <!-- 授权账号表格 -->
        <el-tab-pane label="授权账号" name="auth">
          <div class="table-section">
            <div class="table-header">
              <h3>授权账号管理 ({{ authAccounts.length }})</h3>

            </div>
            <el-table :data="authAccounts" stripe style="width: 100%" v-loading="loading.auth">
              <el-table-column prop="ghz_name" label="账号名称" min-width="150" />
              <el-table-column prop="ghz_appid" label="AppID" width="200" show-overflow-tooltip>
                <template #default="{ row }">
                  <span v-if="row.ghz_appid">{{ row.ghz_appid }}</span>
                  <el-tag v-else type="warning" size="small">未配置</el-tag>
                </template>
              </el-table-column>

              <el-table-column label="授权状态" width="100">
                <template #default="{ row }">
                  <el-tag :type="row.auth_status === 1 ? 'success' : 'danger'" size="small">
                    {{ row.auth_status === 1 ? '正常' : '异常' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="advertiser_count" label="广告商" width="80" align="center" />
              <el-table-column prop="ad_count" label="广告数" width="80" align="center" />
              <el-table-column prop="preview_count" label="推广数" width="80" align="center" />
              <el-table-column prop="update_time" label="最后更新" width="160">
                <template #default="{ row }">
                  {{ formatTime(row.update_time) }}
                </template>
              </el-table-column>

            </el-table>
          </div>
        </el-tab-pane>

        <!-- 广告商表格 -->
        <el-tab-pane label="广告商" name="advertisers">
          <div class="table-section">
            <div class="table-header">
              <h3>广告商数据 ({{ advertisers.length }})</h3>
              <div class="table-actions">
                <el-input v-model="advertiserSearch.keyword" placeholder="搜索企业名称、品牌、联系人" size="small"
                  style="width: 200px; margin-right: 10px;" clearable @keyup.enter="handleAdvertiserSearch">
                  <template #prefix>
                    <el-icon>
                      <Search />
                    </el-icon>
                  </template>
                </el-input>
                <el-select v-model="advertiserSearch.system_status" placeholder="状态筛选" size="small"
                  style="width: 120px; margin-right: 10px;" clearable>
                  <el-option label="全部" value="" />
                  <el-option label="正常" value="CUSTOMER_STATUS_NORMAL" />
                  <el-option label="待审核" value="CUSTOMER_STATUS_PENDING" />
                  <el-option label="审核拒绝" value="CUSTOMER_STATUS_DENIED" />
                </el-select>
                <el-button type="primary" size="small" @click="handleAdvertiserSearch">
                  搜索
                </el-button>
                <el-button type="success" size="small" @click="syncAllAdvertisers" :loading="syncLoading.advertisers">
                  <el-icon>
                    <Refresh />
                  </el-icon>
                  同步所有
                </el-button>
              </div>
            </div>
            <el-table :data="filteredAdvertisers" stripe style="width: 100%" v-loading="loading.advertisers">
              <el-table-column prop="corporation_name" label="企业名称" min-width="200" show-overflow-tooltip />
              <el-table-column prop="account_id" label="账户ID" width="120" />
              <el-table-column prop="brand_name" label="品牌名称" width="150" show-overflow-tooltip>
                <template #default="{ row }">
                  <span v-if="row.brand_name">{{ row.brand_name }}</span>
                  <span v-else class="empty-text">-</span>
                </template>
              </el-table-column>
              <el-table-column label="状态" width="100">
                <template #default="{ row }">
                  <el-tag :type="getStatusType(row.system_status)" size="small">
                    {{ getStatusText(row.system_status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="daily_budget" label="日预算" width="120">
                <template #default="{ row }">
                  {{ formatBudget(row.daily_budget) }}
                </template>
              </el-table-column>
              <el-table-column prop="ad_count" label="广告数" width="80" align="center" />
              <el-table-column prop="preview_count" label="推广数" width="80" align="center" />
              <el-table-column prop="contact_person" label="联系人" width="100">
                <template #default="{ row }">
                  <span v-if="row.contact_person">{{ row.contact_person }}</span>
                  <span v-else class="empty-text">-</span>
                </template>
              </el-table-column>
              <el-table-column prop="last_sync_time" label="最后同步" width="160">
                <template #default="{ row }">
                  {{ formatTime(row.last_sync_time) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-tab-pane>

        <!-- 广告数据表格 -->
        <el-tab-pane label="广告数据" name="ads">
          <div class="table-section">
            <div class="table-header">
              <h3>广告投放数据 ({{ filteredAds.length }})</h3>
              <div class="table-actions">
                <el-input v-model="adSearch.keyword" placeholder="搜索广告名称" size="small"
                  style="width: 200px; margin-right: 10px;" clearable @keyup.enter="handleAdSearch">
                  <template #prefix>
                    <el-icon>
                      <Search />
                    </el-icon>
                  </template>
                </el-input>
                <el-select v-model="adSearch.system_status" placeholder="状态筛选" size="small"
                  style="width: 120px; margin-right: 10px;" clearable>
                  <el-option label="全部" value="" />
                  <el-option label="活跃" value="ADGROUP_STATUS_ACTIVE" />
                  <el-option label="暂停" value="ADGROUP_STATUS_SUSPEND" />
                  <el-option label="待审核" value="ADGROUP_STATUS_PENDING" />
                </el-select>
                <el-button type="primary" size="small" @click="handleAdSearch">
                  搜索
                </el-button>
                <el-button type="success" size="small" @click="syncAllAds" :loading="syncLoading.ads">
                  <el-icon>
                    <Refresh />
                  </el-icon>
                  同步所有
                </el-button>
              </div>
            </div>
            <el-table :data="filteredAds" stripe style="width: 100%" v-loading="loading.ads">
              <el-table-column prop="adgroup_name" label="广告名称" min-width="200" show-overflow-tooltip />
              <el-table-column prop="adgroup_id" label="广告ID" width="120" />
              <el-table-column label="状态" width="100">
                <template #default="{ row }">
                  <el-tag :type="getAdStatusType(row.system_status)" size="small">
                    {{ getAdStatusText(row.system_status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="daily_budget" label="日预算" width="120">
                <template #default="{ row }">
                  {{ formatBudget(row.daily_budget) }}
                </template>
              </el-table-column>
              <el-table-column prop="bid_amount" label="出价" width="100">
                <template #default="{ row }">
                  <span v-if="row.bid_amount">¥{{ row.bid_amount }}</span>
                  <span v-else class="empty-text">-</span>
                </template>
              </el-table-column>
              <el-table-column prop="optimization_goal" label="优化目标" width="150" show-overflow-tooltip>
                <template #default="{ row }">
                  <span v-if="row.optimization_goal">{{ getOptimizationGoalText(row.optimization_goal) }}</span>
                  <span v-else class="empty-text">-</span>
                </template>
              </el-table-column>
              <el-table-column prop="preview_count" label="推广次数" width="100" align="center" />
              <el-table-column prop="corporation_name" label="所属企业" width="150" show-overflow-tooltip />
              <el-table-column prop="last_sync_time" label="最后同步" width="160">
                <template #default="{ row }">
                  {{ formatTime(row.last_sync_time) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-tab-pane>

        <!-- 推广记录表格 -->
        <el-tab-pane label="推广记录" name="previews">
          <div class="table-section">
            <div class="table-header">
              <h3>定向推广记录 ({{ previewStats.total }})</h3>
              <div class="table-stats">
                <el-tag type="success">成功: {{ previewStats.success }}</el-tag>
                <el-tag type="danger">失败: {{ previewStats.fail }}</el-tag>
                <el-tag type="info">总计: {{ previewStats.total }}</el-tag>
              </div>
            </div>
            <el-table :data="previewLogs" stripe style="width: 100%" v-loading="loading.previews">
              <el-table-column prop="adgroup_name" label="广告名称" min-width="180" show-overflow-tooltip />
              <el-table-column prop="corporation_name" label="广告商" width="150" show-overflow-tooltip />
              <el-table-column label="推广结果" width="120">
                <template #default="{ row }">
                  <el-tag :type="row.error_message ? 'danger' : 'success'" size="small">
                    {{ row.error_message ? '失败' : '成功' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="用户数量" width="100" align="center">
                <template #default="{ row }">
                  {{ row.user_id_list ? row.user_id_list.length : 0 }}
                </template>
              </el-table-column>
              <el-table-column label="成功/失败" width="120" align="center">
                <template #default="{ row }">
                  <span v-if="!row.error_message" class="success-text">
                    {{ row.success_count }}/{{ row.fail_count }}
                  </span>
                  <span v-else class="error-text">-</span>
                </template>
              </el-table-column>
              <el-table-column prop="create_time" label="推广时间" width="160">
                <template #default="{ row }">
                  {{ formatTime(row.create_time) }}
                </template>
              </el-table-column>

            </el-table>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 底部统计 -->
    <div class="footer-stats">
      <el-row :gutter="20">
        <el-col :xs="12" :sm="6">
          <div class="footer-stat">
            <div class="footer-value">{{ overviewData.syncSuccessRate || '0%' }}</div>
            <div class="footer-label">同步成功率</div>
          </div>
        </el-col>
        <el-col :xs="12" :sm="6">
          <div class="footer-stat">
            <div class="footer-value">{{ overviewData.avgDailyPreviews || 0 }}</div>
            <div class="footer-label">日均推广</div>
          </div>
        </el-col>
        <el-col :xs="12" :sm="6">
          <div class="footer-stat">
            <div class="footer-value">{{ overviewData.activeAdRate || '0%' }}</div>
            <div class="footer-label">广告活跃率</div>
          </div>
        </el-col>
        <el-col :xs="12" :sm="6">
          <div class="footer-stat">
            <div class="footer-value">{{ overviewData.totalBudget || 0 }}</div>
            <div class="footer-label">总预算(万)</div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import * as echarts from 'echarts';
import {
  Refresh, User, OfficeBuilding, Promotion, DataAnalysis,
  Search
} from '@element-plus/icons-vue'

export default {
  name: 'TxAdDashboard',
  components: {
    Refresh, User, OfficeBuilding, Promotion, DataAnalysis, Search
  },
  data() {
    return {
      activeTab: 'auth',
      refreshing: false,
      loading: {
        auth: false,
        advertisers: false,
        ads: false,
        previews: false
      },
      syncLoading: {
        auth: false,
        advertisers: false,
        ads: false
      },
      overviewData: {},
      authAccounts: [],
      advertisers: [],
      ads: [],
      previewLogs: [],
      advertiserSearch: {
        keyword: '',
        system_status: ''
      },
      adSearch: {
        keyword: '',
        system_status: ''
      },
      previewStats: {
        success: 0,
        fail: 0,
        total: 0
      },
      charts: {
        advertiserStatus: null,
        adStatus: null,
        previewTrend: null
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    filteredAdvertisers() {
      let filtered = this.advertisers;

      if (this.advertiserSearch.keyword) {
        const keyword = this.advertiserSearch.keyword.toLowerCase();
        filtered = filtered.filter(item =>
          (item.corporation_name && item.corporation_name.toLowerCase().includes(keyword)) ||
          (item.brand_name && item.brand_name.toLowerCase().includes(keyword)) ||
          (item.contact_person && item.contact_person.toLowerCase().includes(keyword))
        );
      }

      if (this.advertiserSearch.system_status) {
        filtered = filtered.filter(item => item.system_status === this.advertiserSearch.system_status);
      }

      return filtered;
    },
    filteredAds() {
      let filtered = this.ads;

      if (this.adSearch.keyword) {
        const keyword = this.adSearch.keyword.toLowerCase();
        filtered = filtered.filter(item =>
          item.adgroup_name.toLowerCase().includes(keyword) ||
          (item.corporation_name && item.corporation_name.toLowerCase().includes(keyword))
        );
      }

      if (this.adSearch.system_status) {
        filtered = filtered.filter(item => item.system_status === this.adSearch.system_status);
      }

      return filtered;
    }
  },
  mounted() {
    this.initDashboard();
    // 延迟初始化图表，确保DOM渲染完成
    setTimeout(() => {
      this.initCharts();
    }, 500);
  },
  beforeUnmount() {
    // 销毁图表实例
    Object.values(this.charts).forEach(chart => {
      if (chart) chart.dispose();
    });
  },
  methods: {
    async initDashboard() {
      this.refreshing = true;
      try {
        await Promise.all([
          this.fetchOverviewData(),
          this.fetchAuthAccounts(),
          this.fetchAdvertisers(),
          this.fetchAds(),
          this.fetchPreviewLogs()
        ]);
      } finally {
        this.refreshing = false;
      }
    },

    async initCharts() {
      await this.$nextTick();
      this.initAdvertiserStatusChart();
      this.initAdStatusChart();
      this.initPreviewTrendChart();
    },

    // 初始化广告商状态图表
    initAdvertiserStatusChart() {
      const chartDom = this.$refs.advertiserStatusChart;
      if (!chartDom) return;

      this.charts.advertiserStatus = echarts.init(chartDom);
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          data: ['正常', '待审核', '审核拒绝']
        },
        series: [
          {
            name: '广告商状态',
            type: 'pie',
            radius: ['50%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false,
              position: 'center'
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 18,
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: [
              {
                value: this.overviewData.normalAdvertisers || 0,
                name: '正常',
                itemStyle: { color: '#67C23A' }
              },
              {
                value: this.overviewData.pendingAdvertisers || 0,
                name: '待审核',
                itemStyle: { color: '#E6A23C' }
              },
              {
                value: this.overviewData.rejectedAdvertisers || 0,
                name: '审核拒绝',
                itemStyle: { color: '#F56C6C' }
              }
            ]
          }
        ]
      };

      this.charts.advertiserStatus.setOption(option);
    },

    // 初始化广告状态图表
    initAdStatusChart() {
      const chartDom = this.$refs.adStatusChart;
      if (!chartDom) return;

      this.charts.adStatus = echarts.init(chartDom);
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['活跃', '暂停', '待审核'],
          axisLine: {
            lineStyle: {
              color: '#DCDFE6'
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: true,
            lineStyle: {
              color: '#DCDFE6'
            }
          }
        },
        series: [
          {
            name: '广告数量',
            type: 'bar',
            barWidth: '60%',
            itemStyle: {
              color: function (params) {
                const colors = ['#67C23A', '#E6A23C', '#909399'];
                return colors[params.dataIndex] || '#409EFF';
              }
            },
            data: [
              this.overviewData.activeAds || 0,
              this.overviewData.suspendedAds || 0,
              this.overviewData.pendingAds || 0
            ]
          }
        ]
      };

      this.charts.adStatus.setOption(option);
    },

    // 初始化推广趋势图表
    initPreviewTrendChart() {
      const chartDom = this.$refs.previewTrendChart;
      if (!chartDom) return;

      this.charts.previewTrend = echarts.init(chartDom);

      // 使用真实数据
      const trendData = this.overviewData.trendData || [];
      const dates = trendData.map(item => {
        const date = new Date(item.date);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      });
      const previews = trendData.map(item => item.previews);

      const option = {
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates.length > 0 ? dates : ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '推广次数',
            type: 'line',
            smooth: true,
            lineStyle: {
              width: 3,
              color: '#409EFF'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [{
                  offset: 0, color: 'rgba(64, 158, 255, 0.3)'
                }, {
                  offset: 1, color: 'rgba(64, 158, 255, 0.1)'
                }]
              }
            },
            data: previews.length > 0 ? previews : [0, 0, 0, 0, 0, 0, 0]
          }
        ]
      };

      this.charts.previewTrend.setOption(option);
    },

    // 获取概览数据
    async fetchOverviewData() {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard.php`, {
          action: 'get_overview',
          store_id: this.userInfo.id
        });

        if (response.data.success) {
          this.overviewData = response.data.data;
          // 更新图表数据
          this.updateCharts();
        }
      } catch (error) {
        console.error('获取概览数据失败:', error);
        this.$message.error('获取概览数据失败');
      }
    },

    // 获取授权账号
    async fetchAuthAccounts() {
      this.loading.auth = true;
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard.php`, {
          action: 'get_auth_accounts',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 50
        });

        if (response.data.success) {
          this.authAccounts = response.data.data;
        }
      } catch (error) {
        console.error('获取授权账号失败:', error);
        this.$message.error('获取授权账号失败');
      } finally {
        this.loading.auth = false;
      }
    },

    // 获取广告商数据
    async fetchAdvertisers() {
      this.loading.advertisers = true;
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard.php`, {
          action: 'get_advertisers',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 50
        });

        if (response.data.success) {
          this.advertisers = response.data.data;
        }
      } catch (error) {
        console.error('获取广告商数据失败:', error);
        this.$message.error('获取广告商数据失败');
      } finally {
        this.loading.advertisers = false;
      }
    },

    // 获取广告数据
    async fetchAds() {
      this.loading.ads = true;
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard.php`, {
          action: 'get_ads',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 50
        });

        if (response.data.success) {
          this.ads = response.data.data;
        }
      } catch (error) {
        console.error('获取广告数据失败:', error);
        this.$message.error('获取广告数据失败');
      } finally {
        this.loading.ads = false;
      }
    },

    // 获取推广记录
    async fetchPreviewLogs() {
      this.loading.previews = true;
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/dashboard.php`, {
          action: 'get_preview_logs',
          store_id: this.userInfo.id,
          page: 1,
          page_size: 20
        });

        if (response.data.success) {
          this.previewLogs = response.data.data;
          this.calculatePreviewStats();
        }
      } catch (error) {
        console.error('获取推广记录失败:', error);
        this.$message.error('获取推广记录失败');
      } finally {
        this.loading.previews = false;
      }
    },

    // 更新图表数据
    updateCharts() {
      if (this.charts.advertiserStatus) {
        this.charts.advertiserStatus.setOption({
          series: [{
            data: [
              { value: this.overviewData.normalAdvertisers || 0, name: '正常' },
              { value: this.overviewData.pendingAdvertisers || 0, name: '待审核' },
              { value: this.overviewData.rejectedAdvertisers || 0, name: '审核拒绝' }
            ]
          }]
        });
      }

      if (this.charts.adStatus) {
        this.charts.adStatus.setOption({
          series: [{
            data: [
              this.overviewData.activeAds || 0,
              this.overviewData.suspendedAds || 0,
              this.overviewData.pendingAds || 0
            ]
          }]
        });
      }

      if (this.charts.previewTrend) {
        const trendData = this.overviewData.trendData || [];
        const dates = trendData.map(item => {
          const date = new Date(item.date);
          return `${date.getMonth() + 1}/${date.getDate()}`;
        });
        const previews = trendData.map(item => item.previews);

        this.charts.previewTrend.setOption({
          xAxis: {
            data: dates.length > 0 ? dates : ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
          },
          series: [{
            data: previews.length > 0 ? previews : [0, 0, 0, 0, 0, 0, 0]
          }]
        });
      }
    },

    // 计算推广统计
    calculatePreviewStats() {
      this.previewStats = {
        success: this.previewLogs.filter(log => !log.error_message).length,
        fail: this.previewLogs.filter(log => log.error_message).length,
        total: this.previewLogs.length
      };
    },

    // 获取活跃授权账号数量
    getActiveAuthCount() {
      return this.authAccounts.filter(account => account.auth_status === 1).length;
    },

    // 刷新所有数据
    async refreshAllData() {
      await this.initDashboard();
      this.$message.success('数据刷新成功');
    },





    // 同步所有广告商
    async syncAllAdvertisers() {
      this.syncLoading.advertisers = true;
      try {
        // 调用同步接口
        this.$message.success('广告商数据同步完成');
        await this.fetchAdvertisers();
      } catch (error) {
        this.$message.error('同步失败');
      } finally {
        this.syncLoading.advertisers = false;
      }
    },

    // 同步所有广告
    async syncAllAds() {
      this.syncLoading.ads = true;
      try {
        // 调用同步接口
        this.$message.success('广告数据同步完成');
        await this.fetchAds();
      } catch (error) {
        this.$message.error('同步失败');
      } finally {
        this.syncLoading.ads = false;
      }
    },

    // 广告商搜索
    handleAdvertiserSearch() {
      // 搜索逻辑已在 computed 中实现
    },

    // 广告搜索
    handleAdSearch() {
      // 搜索逻辑已在 computed 中实现
    },



    // 工具方法
    getTokenStatusType(status) {
      const typeMap = {
        'valid': 'success',
        'expiring': 'warning',
        'invalid': 'danger'
      };
      return typeMap[status] || 'info';
    },

    getTokenStatusText(status) {
      const textMap = {
        'valid': '有效',
        'expiring': '即将过期',
        'invalid': '无效'
      };
      return textMap[status] || '未知';
    },

    getStatusType(status) {
      const typeMap = {
        'CUSTOMER_STATUS_NORMAL': 'success',
        'CUSTOMER_STATUS_PENDING': 'warning',
        'CUSTOMER_STATUS_DENIED': 'danger'
      };
      return typeMap[status] || 'info';
    },

    getStatusText(status) {
      const textMap = {
        'CUSTOMER_STATUS_NORMAL': '正常',
        'CUSTOMER_STATUS_PENDING': '待审核',
        'CUSTOMER_STATUS_DENIED': '审核拒绝'
      };
      return textMap[status] || status;
    },

    getAdStatusType(status) {
      const typeMap = {
        'ADGROUP_STATUS_ACTIVE': 'success',
        'ADGROUP_STATUS_SUSPEND': 'warning',
        'ADGROUP_STATUS_PENDING': 'info'
      };
      return typeMap[status] || 'info';
    },

    getAdStatusText(status) {
      const textMap = {
        'ADGROUP_STATUS_ACTIVE': '活跃',
        'ADGROUP_STATUS_SUSPEND': '暂停',
        'ADGROUP_STATUS_PENDING': '待审核'
      };
      return textMap[status] || status;
    },

    getOptimizationGoalText(goal) {
      const goalMap = {
        'OPTIMIZATIONGOAL_ECOMMERCE_ORDER': '电商订单',
        'OPTIMIZATIONGOAL_IMPRESSION': '展示量',
        'OPTIMIZATIONGOAL_CLICK': '点击量',
        'OPTIMIZATIONGOAL_CONVERSION': '转化量'
      };
      return goalMap[goal] || goal;
    },

    formatBudget(budget) {
      if (!budget || budget === 0) return '未设置';
      if (budget >= 10000) {
        return `¥${(budget / 10000).toFixed(1)}万`;
      }
      return `¥${budget}`;
    },

    formatTime(time) {
      if (!time) return '-';
      return new Date(time).toLocaleString('zh-CN');
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-container {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  min-height: 100vh;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  .header-main {
    .dashboard-title {
      font-size: 28px;
      font-weight: 700;
      color: #1f2937;
      margin: 0 0 8px 0;
      background: linear-gradient(135deg, #409EFF, #67C2EF);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .dashboard-subtitle {
      font-size: 16px;
      color: #606266;
      margin: 0;
    }
  }

  .header-actions {
    display: flex;
    gap: 12px;
  }
}

.overview-cards {
  margin-bottom: 24px;

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;
    border-left: 4px solid;
    cursor: pointer;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }

    &.primary {
      border-left-color: #409EFF;

      .card-icon {
        background: linear-gradient(135deg, #409EFF, #67C2EF);
      }
    }

    &.success {
      border-left-color: #67C23A;

      .card-icon {
        background: linear-gradient(135deg, #67C23A, #85CE61);
      }
    }

    &.warning {
      border-left-color: #E6A23C;

      .card-icon {
        background: linear-gradient(135deg, #E6A23C, #EBB563);
      }
    }

    &.danger {
      border-left-color: #F56C6C;

      .card-icon {
        background: linear-gradient(135deg, #F56C6C, #F78989);
      }
    }

    .card-icon {
      width: 60px;
      height: 60px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      color: white;
      font-size: 28px;
    }

    .card-content {
      .card-value {
        font-size: 32px;
        font-weight: 700;
        color: #1f2937;
        margin-bottom: 4px;
      }

      .card-label {
        font-size: 14px;
        color: #606266;
        font-weight: 600;
      }

      .card-desc {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
      }
    }

    .card-trend {
      position: absolute;
      top: 20px;
      right: 20px;
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
      font-weight: 600;

      .trend-up {
        color: #67C23A;
      }

      .trend-down {
        color: #F56C6C;
      }

      .trend-neutral {
        color: #909399;
      }
    }
  }
}

.charts-section {
  margin-bottom: 24px;

  .chart-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    height: 100%;

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1f2937;
      }
    }

    .chart-container {
      position: relative;
    }
  }
}

.data-tables {
  margin-bottom: 24px;

  .dashboard-tabs {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

    :deep(.el-tabs__header) {
      background: linear-gradient(135deg, #f8fafc 0%, #e4e7ed 100%);
      margin: 0;
    }

    :deep(.el-tabs__item) {
      font-weight: 600;
    }
  }

  .table-section {
    padding: 0;

    .table-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid #ebeef5;
      background: #fafafa;

      h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #1f2937;
      }

      .table-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .table-stats {
        display: flex;
        gap: 12px;

        .stat-item {
          font-size: 14px;
          color: #606266;
        }
      }
    }
  }
}

.footer-stats {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  .footer-stat {
    text-align: center;
    padding: 16px;

    .footer-value {
      font-size: 24px;
      font-weight: 700;
      color: #409EFF;
      margin-bottom: 8px;
    }

    .footer-label {
      font-size: 14px;
      color: #606266;
    }
  }
}

// 文本样式
.empty-text {
  color: #909399;
  font-style: italic;
}

.success-text {
  color: #67C23A;
  font-weight: 500;
}

.error-text {
  color: #F56C6C;
  font-weight: 500;
}

// 响应式设计
@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .table-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch !important;

    .table-actions {
      justify-content: center;
      flex-wrap: wrap;
    }
  }

  .overview-cards {
    .stat-card {
      padding: 16px;

      .card-value {
        font-size: 24px;
      }
    }
  }
}

// 动画效果
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// 加载状态
:deep(.el-loading-mask) {
  border-radius: 8px;
}
</style>